import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { collaboratorsApi, type Collaborator } from '../api/collaborators';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { useToast } from '../components/ui/Toast';
import { useHousehold } from '../hooks/useHousehold';
import { useAuthContext } from '../contexts/AuthContext';
import { ShareIcon, UserPlusIcon } from '../components/ui/icons';

export function CollaboratorsScreen() {
  const { householdId } = useParams<{ householdId: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuthContext();
  const { data: household } = useHousehold();

  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<Collaborator | null>(null);

  const id = householdId ?? household?.id ?? '';
  const isOwner = household?.ownerId === user?.userId;

  const { data: collaborators, isLoading } = useQuery({
    queryKey: ['collaborators', id],
    queryFn: () => collaboratorsApi.list(id),
    enabled: Boolean(id),
    staleTime: 30_000,
  });

  const inviteMutation = useMutation({
    mutationFn: () => collaboratorsApi.invite(id),
    onSuccess: (data) => {
      setInviteCode(data.inviteCode);
      setShareUrl(data.shareUrl);
      navigator.clipboard.writeText(data.shareUrl).catch(() => null);
      addToast('Link de convite copiado!', 'success');
    },
    onError: () => addToast('Erro ao gerar convite.', 'error'),
  });

  const revokeMutation = useMutation({
    mutationFn: (collaboratorId: string) => collaboratorsApi.revoke(id, collaboratorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collaborators', id] });
      addToast('Colaborador removido.', 'success');
      setRevokeTarget(null);
    },
    onError: () => addToast('Erro ao remover colaborador.', 'error'),
  });

  if (!isOwner) {
    return (
      <div className="flex flex-col min-h-screen pb-[calc(4rem+env(safe-area-inset-bottom)+1.5rem)]">
        <PageHeader title="Colaboradores" showBack onBack={() => navigate(-1)} />
        <div className="flex-1 flex items-center justify-center px-5 text-center">
          <p className="text-gray-500 text-sm">Apenas o dono da lista pode gerenciar colaboradores.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen pb-[calc(4rem+env(safe-area-inset-bottom)+1.5rem)]">
      <PageHeader title="Colaboradores" showBack onBack={() => navigate(-1)} />

      <div className="flex-1 px-4 py-5 space-y-5">

        {/* Invite section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-3">
          <p className="text-sm font-semibold text-gray-900">Convidar pessoa</p>
          <p className="text-sm text-gray-500">
            Gere um link de convite para compartilhar sua lista.
          </p>

          {shareUrl && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
              <p className="text-xs text-gray-500 break-all">{shareUrl}</p>
            </div>
          )}

          <Button
            variant="secondary"
            fullWidth
            onClick={() => inviteMutation.mutate()}
            loading={inviteMutation.isPending}
            title={shareUrl ? 'Gerar novo convite' : 'Gerar link de convite'}
            aria-label={shareUrl ? 'Gerar novo convite' : 'Gerar link de convite'}
          >
            <UserPlusIcon size={16} />
          </Button>

          {shareUrl && (
            <a
              href={`https://wa.me/?text=${encodeURIComponent('Entre na minha lista do HomeStock: ' + shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full h-12 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-xl text-sm transition-colors touch-manipulation"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              Compartilhar via WhatsApp
            </a>
          )}

          {inviteCode && (
            <div className="text-center">
              <p className="text-xs text-gray-400">Código: <span className="font-mono font-bold text-gray-700">{inviteCode}</span></p>
            </div>
          )}
        </div>

        {/* Collaborators list */}
        <div>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1 mb-2">
            Colaboradores ativos
          </p>
          {isLoading ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 flex justify-center">
              <div className="animate-spin w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
            </div>
          ) : !collaborators || collaborators.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 text-center">
              <p className="text-sm text-gray-500">Nenhum colaborador ainda.</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {collaborators.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between py-3 px-4 border-b border-gray-100 last:border-0"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{c.name}</p>
                    <p className="text-xs text-gray-500 truncate">{c.email}</p>
                  </div>
                  <button
                    onClick={() => setRevokeTarget(c)}
                    className="text-xs text-red-500 font-medium ml-3 touch-manipulation flex-shrink-0"
                  >
                    Revogar
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={revokeTarget !== null}
        onClose={() => setRevokeTarget(null)}
        onConfirm={() => revokeTarget && revokeMutation.mutate(revokeTarget.id)}
        title="Revogar acesso"
        message={revokeTarget ? `Remover ${revokeTarget.name} da lista?` : ''}
        confirmLabel="Revogar"
        confirmVariant="danger"
        loading={revokeMutation.isPending}
      />
    </div>
  );
}
